/**
 * Custom Astral payment marks.
 *
 * These replace the star and flag emoji in the Premium payment UI. Both are drawn
 * from the same moon-halo family the payment titles and selected plan states
 * use — pearl core, blush mid-tone, dusty-rose edge — so they read as Astral UI
 * assets rather than platform emoji.
 *
 * They share stroke weight, gradient construction and glow language, but stay
 * distinct in form: the Stars mark is pointed and crystalline, the Toman mark is
 * a circular medallion.
 *
 * Colour flows from the shared `--neb-*` tokens, so both recolour with the
 * theme; the glow is a shape-aware `drop-shadow` applied in CSS, never a box.
 */

export interface PaymentIconProps {
  /** Rendered size in px (square). */
  size?: number;
  className?: string;
}

/** Unique gradient ids per icon so two instances on one page never collide. */
const STAR_FILL = 'astralStarFill';
const STAR_CORE = 'astralStarCore';
const COIN_RING = 'astralCoinRing';
const COIN_FACE = 'astralCoinFace';

/**
 * Telegram Stars — an elongated four-point crystalline star: tall vertical
 * points, shorter horizontal ones, concave waists and a small bright core.
 */
export function PremiumStarsIcon({ size = 20, className }: PaymentIconProps): JSX.Element {
  return (
    <svg
      className={`pay-icon pay-icon--star${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={STAR_FILL} x1="0.3" y1="0" x2="0.7" y2="1">
          <stop offset="0%" stopColor="var(--neb-pearl)" />
          <stop offset="52%" stopColor="#FFB4D5" />
          <stop offset="100%" stopColor="var(--neb-dusty-rose)" />
        </linearGradient>
        <radialGradient id={STAR_CORE} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="60%" stopColor="var(--neb-pearl)" stopOpacity="0.85" />
          <stop offset="100%" stopColor="var(--neb-pearl)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Vertical points run to the edges; horizontal points stop short, which
          is what gives the mark its elongated, jewel-like proportion. */}
      <path
        d="M12 1.2c.62 5.42 2.36 7.5 8.1 8.35-5.74.85-7.48 2.93-8.1 8.35-.62-5.42-2.36-7.5-8.1-8.35 5.74-.85 7.48-2.93 8.1-8.35Z"
        transform="translate(0 2.5) scale(1 1.16) translate(0 -2.5)"
        fill={`url(#${STAR_FILL})`}
      />
      {/* Faceted edge light along the upper-left flank. */}
      <path
        d="M12 2.6c.5 4.3 1.7 6.2 5.6 7.2"
        stroke="#FFFFFF"
        strokeOpacity="0.75"
        strokeWidth="0.7"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="12" cy="12" r="2.6" fill={`url(#${STAR_CORE})`} />
      <circle cx="12" cy="12" r="0.75" fill="#FFFFFF" />
    </svg>
  );
}

/**
 * Toman — a thin luminous medallion with an inner faceted diamond and a small
 * central seal. Deliberately carries no currency glyph, text or national
 * emblem: it reads as "payment" through form alone.
 */
export function TomanPaymentIcon({ size = 20, className }: PaymentIconProps): JSX.Element {
  return (
    <svg
      className={`pay-icon pay-icon--coin${className ? ` ${className}` : ''}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={COIN_RING} x1="0.25" y1="0" x2="0.75" y2="1">
          <stop offset="0%" stopColor="var(--neb-pearl)" />
          <stop offset="55%" stopColor="#FFB4D5" />
          <stop offset="100%" stopColor="var(--neb-dusty-rose)" />
        </linearGradient>
        <radialGradient id={COIN_FACE} cx="0.38" cy="0.32" r="0.75">
          <stop offset="0%" stopColor="var(--neb-pearl)" stopOpacity="0.22" />
          <stop offset="70%" stopColor="var(--neb-dusty-rose)" stopOpacity="0.1" />
          <stop offset="100%" stopColor="var(--neb-dusty-rose)" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Faint face wash, then the thin luminous rim. */}
      <circle cx="12" cy="12" r="9.1" fill={`url(#${COIN_FACE})`} />
      <circle cx="12" cy="12" r="9.1" stroke={`url(#${COIN_RING})`} strokeWidth="1.3" fill="none" />
      {/* Inner hairline, matching the sparkle language elsewhere in Astral. */}
      <circle cx="12" cy="12" r="6.5" stroke="var(--neb-dusty-rose)" strokeOpacity="0.55" strokeWidth="0.6" fill="none" />
      {/* Faceted diamond + central seal. */}
      <path
        d="M12 6.9 15.1 12 12 17.1 8.9 12 12 6.9Z"
        stroke={`url(#${COIN_RING})`}
        strokeWidth="1"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="12" cy="12" r="1.5" fill="var(--neb-pearl)" fillOpacity="0.9" />
      {/* A single highlight on the upper-left rim, as on the star. */}
      <path
        d="M5.6 8.4A7.6 7.6 0 0 1 10.4 4.2"
        stroke="#FFFFFF"
        strokeOpacity="0.6"
        strokeWidth="0.7"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
